import type { ColumnConfig } from "@shared/components/GridV2/SnGridTypes";

type ProjectValue = {
  nm_pjt?: string;
  value?: string | number | null;
};

// PostgreSQL JSONB 래퍼 또는 JSON 문자열을 프로젝트 객체로 변환한다.
const parseProjectValues = (
  rawValue: any
): Record<string, ProjectValue> => {
  if (!rawValue) return {};

  try {
    const value = rawValue?.type === "jsonb" ? rawValue.value : rawValue;
    return typeof value === "string" ? JSON.parse(value) : value;
  } catch {
    return {};
  }
};

// 기존 Grid 메타데이터를 재사용해 동적 Pivot 컬럼 설정을 생성한다.
const createPivotColumn = (
  template: Partial<ColumnConfig>,
  cdColumn: string,
  nmColumn: string,
  noSeq: number,
  tpColumn: string,
  width: number,
  ynSum: string = "N" // 숫자 컬럼에 "Y" 를 주면 하단 소계행(SnGridFooter)에 합산된다.
): ColumnConfig => ({
  ...template,
  cd_firm: template.cd_firm ?? "",
  cd_menu: template.cd_menu ?? "SN_PMS_PJT_REG_022",
  cd_grid: template.cd_grid ?? "grid3",
  cd_user: template.cd_user ?? "",
  cd_column: cdColumn,
  nm_column: nmColumn,
  tp_column: tpColumn,
  fg_visible: "Y",
  no_seq: noSeq,
  qt_width: width,
  yn_required: "N",
  yn_allowsorting: "Y",
  yn_fixed: "N",
  yn_sum: ynSum,
  dc_editcon: "false",
});

export const createPivotGridData = (
  rows: any[],
  baseColumns: ColumnConfig[]
): { columns: ColumnConfig[]; rows: any[] } => {
  // 프로젝트 데이터는 한 번만 파싱해 동적 컬럼과 행 생성에 함께 사용한다.
  const parsedRows = (rows ?? []).map((row) => ({
    row,
    projects: parseProjectValues(row.project_values),
  }));
  const projectNames = new Map<string, string>();

  // 전체 사원의 프로젝트를 합쳐 Pivot에 표시할 컬럼 목록을 만든다.
  parsedRows.forEach(({ projects }) => {
    Object.entries(projects).forEach(([cdPjt, project]) => {
      projectNames.set(cdPjt, project?.nm_pjt || cdPjt);
    });
  });

  const projectCodes = Array.from(projectNames.keys()).sort();
  // sys_formgridcolumn 에 grid3 정의가 없으면 baseColumns 가 undefined 로 넘어온다.
  // 이 경우에도 Pivot 컬럼은 기본값으로 만들어져야 하므로 빈 배열로 정규화한다.
  const safeBaseColumns = baseColumns ?? [];
  const baseColumnMap = new Map(
    safeBaseColumns.map((column) => [column.cd_column, column])
  );
  const defaultTemplate = safeBaseColumns[0] ?? {};
  const numberTemplate = baseColumnMap.get("total_value") ?? defaultTemplate;

  // sys_gridctrl에 등록된 기본 컬럼 설정이 있으면 우선 적용한다.
  const getBaseColumn = (
    field: string,
    label: string,
    type: string,
    width: number,
    sequence: number
  ) => {
    const baseColumn = baseColumnMap.get(field);
    return createPivotColumn(
      baseColumn ?? defaultTemplate,
      field,
      baseColumn?.nm_column || label,
      sequence,
      baseColumn?.tp_column || type,
      baseColumn?.qt_width || width
    );
  };

  let sequence = 1;
  const pivotColumns: ColumnConfig[] = [
    getBaseColumn("cd_emp", "사원코드", "SnTextBox", 100, sequence++),
    getBaseColumn("nm_emp", "사원명", "SnTextBox", 120, sequence++),
    ...projectCodes.map((cdPjt) =>
      createPivotColumn(
        numberTemplate,
        `pjt_${cdPjt}`,
        projectNames.get(cdPjt) || cdPjt,
        sequence++,
        "SnNumberBox",
        numberTemplate.qt_width || 120,
        "Y" // 프로젝트별 소계
      )
    ),
    createPivotColumn(
      numberTemplate,
      "total_value",
      baseColumnMap.get("total_value")?.nm_column || "합계",
      sequence++,
      "SnNumberBox",
      baseColumnMap.get("total_value")?.qt_width || 100,
      "Y" // 합계 컬럼 소계(총계)
    ),
  ];

  // project_values를 제거하고 프로젝트별 값을 pjt_{프로젝트코드} 필드로 펼친다.
  const pivotRows = parsedRows.map(({ row, projects }, index) => {
    const pivotRow: any = {
      ...row,
      no: row.no ?? index + 1,
    };
    delete pivotRow.project_values;

    projectCodes.forEach((cdPjt) => {
      const rawProjectValue = projects[cdPjt]?.value;
      const numericValue = Number(rawProjectValue);
      pivotRow[`pjt_${cdPjt}`] =
        rawProjectValue !== null &&
        rawProjectValue !== undefined &&
        Number.isFinite(numericValue)
          ? numericValue
          : 0;
    });

    return pivotRow;
  });

  return { columns: pivotColumns, rows: pivotRows };
};
